<script setup lang="ts">
/**
 *
 * @name CertificatesView
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE QUE MUESTRA LA LISTA DE CERTIFICADOS
 *
 * responsabilidades:
 * R1: PERMITE VISUALIZAR LA LISTA DE CERTIFICADOS
 * R2: PERMITE EDITAR UN CERTIFICADO EXISTENTE
 * R3: PERMITE ELIMINAR UN CERTIFICADO
 * R4: PERMITE SELECCIONAR CERTIFICADOS
 * R5: PERMITE MOSTRAR EL ESTADO DE VALIDACIÓN DE LOS CERTIFICADOS
 * R6: PERMITE MANEJAR EL MODO DE PRUEBA
 *
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { ISelectedInstitution } from '@shared/types'
import { ref, onMounted, onUnmounted, nextTick, watch, shallowRef, computed } from 'vue'
import { ref as dbRef, remove } from 'firebase/database'
import { database } from '@/firebase/config'
import { formatDate } from '@/utils/dateFormatter'
import NewCertificateModal from '@/components/certificados/NewCertificateModal.vue'
import { TCertificate } from '@/composables/useCertificateStatus'
import LoadingState from '@/components/common/LoadingState.vue'
import EmptyState from '@/components/common/EmptyState.vue'
import BaseModal from '../BaseModal.vue'
// Importar estilos compartidos
import '@/styles/virtualized-components.css'

/**
 * @description Props del componente
 * @property {TCertificate[]} certificates - Lista de certificados
 * @property {ISelectedInstitution} institution - Institución seleccionada
 * @property {any} currentInstitution - Institución actual
 * @property {boolean} testMode - Indica si el componente está en modo prueba
 */
const props = defineProps<{
	certificates: TCertificate[]
	institution: ISelectedInstitution
	currentInstitution: string
	testMode: boolean
}>()

/**
 * @description Emits del componente
 * @param {string} certificate-removed - Emitido cuando se elimina un certificado
 */

/**
 * @description Referencia a la base de datos de Firebase
 */
const db = database

/**
 * @description Estado reactivo optimizado
 */
const certificates = shallowRef<TCertificate[]>([])
const loading = ref(false)
const showNewCertificateModal = ref(false)
const selectedCertificate = ref<TCertificate | undefined>(undefined)

// Estados de carga mejorados
const isInitializing = ref(true)
const isRemoving = ref<string | null>(null) // ID del elemento que se está eliminando
const removingItems = ref<Set<string>>(new Set()) // Conjunto de IDs que se están eliminando
const preparationMessage = ref('Preparando vista...')

// Virtualización optimizada
const containerRef = ref<HTMLElement>()
const itemHeight = 200 // Altura estimada de cada tarjeta
const visibleItems = ref<TCertificate[]>([])
const scrollTop = ref(0)
const containerHeight = ref(0)
const startIndex = computed(() => Math.floor(scrollTop.value / itemHeight))
const endIndex = computed(() => Math.min(startIndex.value + Math.ceil(containerHeight.value / itemHeight) + 3, certificates.value.length))
const errorMessage = ref<string>('')
const showErrorModal = ref(false)
// Throttling para scroll
let scrollTimeout: number | null = null
const SCROLL_THROTTLE = 16 // ~60fps

/**
 * @description Función que obtiene la ruta de la institución en Firebase
 * @param {string} path - Ruta adicional
 * @returns {string} Ruta completa
 */
const getInstitutionPath = (path: string) => `institutions/${props.currentInstitution}${props.testMode ? '/testMode' : ''}${path}`

// Calcular elementos visibles con throttling
const updateVisibleItems = () => {
	if (!certificates.value.length) {
		visibleItems.value = []
		return
	}

	// Usar requestAnimationFrame para evitar bloqueos
	requestAnimationFrame(() => {
		visibleItems.value = certificates.value.slice(startIndex.value, endIndex.value)
	})
}

// Manejar scroll con throttling
const handleScroll = (event: Event) => {
	if (scrollTimeout) return

	scrollTimeout = window.setTimeout(() => {
		const target = event.target as HTMLElement
		scrollTop.value = target.scrollTop
		updateVisibleItems()
		scrollTimeout = null
	}, SCROLL_THROTTLE)
}

// Actualizar altura del contenedor
const updateContainerHeight = () => {
	if (containerRef.value) {
		containerHeight.value = containerRef.value.clientHeight
		updateVisibleItems()
	}
}

// Observar cambios en el array de certificados con debouncing
let certificatesUpdateTimeout: number | null = null
const updateOnCertificatesChange = () => {
	if (certificatesUpdateTimeout) {
		clearTimeout(certificatesUpdateTimeout)
	}

	certificatesUpdateTimeout = window.setTimeout(() => {
		// Solo actualizar si no hay elementos en proceso de eliminación
		if (removingItems.value.size === 0) {
			certificates.value = props.certificates
			nextTick(() => {
				updateVisibleItems()
			})
		}
	}, 100) // Debounce de 100ms
}

/**
 * @description Función que edita un certificado
 * @param {string} id - ID del certificado a editar
 */
const editCertificate = (id: string) => {
	selectedCertificate.value = props.certificates.find(certificate => certificate.id === id)
	showNewCertificateModal.value = true
}

/**
 * @description Función que elimina un certificado optimizado
 * @param {string} id - ID del certificado a eliminar
 * @async
 * @returns {Promise<void>}
 */
const removeCertificate = async (id: string) => {
	if (!confirm('¿Está seguro de eliminar este certificado?')) return

	try {
		// Marcar el elemento como en proceso de eliminación
		removingItems.value.add(id)
		isRemoving.value = id

		// Actualizar inmediatamente la vista para mostrar el estado de eliminación
		updateVisibleItems()

		const certificateRef = dbRef(db, getInstitutionPath(`/certificates/queue/${id}`))
		await remove(certificateRef)

		// Eliminar inmediatamente de la vista local sin esperar la actualización desde la base de datos
		certificates.value = certificates.value.filter(certificate => certificate.id !== id)
		updateVisibleItems()

		// Limpiar estados de eliminación
		removingItems.value.delete(id)
		isRemoving.value = null

		// Limpiar completamente después de un tiempo para evitar estados inconsistentes
		setTimeout(() => {
			removingItems.value.clear()
			isRemoving.value = null
		}, 1000)
	} catch (error) {
		console.error('Error al remover el certificado:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al remover el certificado'

		// Limpiar estados en caso de error
		removingItems.value.delete(id)
		isRemoving.value = null
	}
}

// Verificar si un elemento está siendo eliminado
const isItemRemoving = (id: string) => removingItems.value.has(id)

// Variables para cleanup
let resizeObserver: ResizeObserver | null = null

// Lifecycle
onMounted(async () => {
	// Simular proceso de inicialización
	preparationMessage.value = 'Cargando certificados...'
	await new Promise(resolve => setTimeout(resolve, 500))

	// Inicializar con los datos actuales
	certificates.value = props.certificates

	preparationMessage.value = 'Configurando vista...'
	await new Promise(resolve => setTimeout(resolve, 300))

	updateContainerHeight()
	updateVisibleItems()
	// Marcar como inicializado
	isInitializing.value = false

	// Resize observer para detectar cambios en el tamaño del contenedor
	resizeObserver = new ResizeObserver(() => {
		updateContainerHeight()
	})

	if (containerRef.value) {
		resizeObserver.observe(containerRef.value)
	}
})

// Cleanup en onUnmounted
onUnmounted(() => {
	if (resizeObserver) {
		resizeObserver.disconnect()
	}
	if (scrollTimeout) {
		clearTimeout(scrollTimeout)
	}
	if (certificatesUpdateTimeout) {
		clearTimeout(certificatesUpdateTimeout)
	}
})

// Watch for certificates changes with debouncing
watch(() => props.certificates, updateOnCertificatesChange, { deep: true })

// Watcher específico para detectar eliminaciones y actualizar inmediatamente
watch(
	() => props.certificates.length,
	(newLength, oldLength) => {
		if (oldLength && newLength < oldLength) {
			// Solo actualizar si no hay elementos en proceso de eliminación
			if (removingItems.value.size === 0) {
				certificates.value = props.certificates
				updateVisibleItems()
			}
		}
	}
)
</script>

<template>
	<div ref="containerRef" @scroll="handleScroll" class="virtualized-container">
		<!-- Indicador de preparación -->
		<LoadingState v-if="isInitializing" :loading-progress="0" :show-progress="false" title="Cargando certificados..." description="Preparando la vista optimizada para mejor rendimiento" />

		<!-- Loading State -->
		<div v-if="loading" class="loading-state">
			<div class="loader"></div>
			<p class="loading-text">Procesando...</p>
		</div>

		<!-- Empty State -->
		<EmptyState
			v-else-if="certificates.length === 0 && !isInitializing"
			icon="school"
			title="No hay certificados registrados"
			description="Los certificados aparecerán aquí una vez que sean agregados al sistema." />

		<!-- Virtualized Certificates Grid -->
		<div v-else class="virtualized-grid">
			<!-- Spacer para mantener el scroll correcto -->
			<div class="virtualized-spacer" :style="{ height: `${startIndex * itemHeight}px` }"></div>

			<!-- Elementos visibles -->
			<div v-for="certificate in visibleItems" :key="certificate.id" class="virtualized-content">
				<VaCard class="certificate-card" :class="{ 'removing': isItemRemoving(certificate.id) }" outlined>
					<!-- Overlay de eliminación -->
					<div v-if="isItemRemoving(certificate.id)" class="removing-overlay">
						<div class="removing-content">
							<div class="removing-spinner"></div>
							<span>Eliminando...</span>
						</div>
					</div>

					<!-- Header -->
					<VaCardTitle class="certificate-header-gradient">
						<div class="certificate-header-content">
							<div class="certificate-student-info">
								<VaAvatar icon="person" class="student-avatar" color="info" size="small" />
								<div class="student-details">
									<h4 class="student-name">
										{{ certificate?.data?.Alumno?.nombre || 'Sin nombre' }}
										{{ certificate?.data?.Alumno?.primerApellido || 'Sin apellido' }}
										{{ certificate?.data?.Alumno?.segundoApellido || 'Sin apellido' }}
									</h4>
									<p class="student-career">
										{{ certificate?.data?.Carrera?.nombreCarrera || 'Carrera no especificada' }}
									</p>
								</div>
							</div>
							<div class="certificate-actions" :class="{ 'disabled': isItemRemoving(certificate.id) }">
								<VaPopover message="Editar certificado">
									<VaButton
										round
										color="primary"
										icon="edit"
										@click="editCertificate(certificate.id)"
										size="small"
										:disabled="isItemRemoving(certificate.id)"
										style="display: flex; align-items: center; justify-content: center" />
								</VaPopover>
								<input type="checkbox" class="custom-checkbox" v-model="certificate.selected" :disabled="isItemRemoving(certificate.id)" />
								<VaPopover message="Eliminar certificado">
									<VaButton
										round
										color="danger"
										icon="delete"
										@click="removeCertificate(certificate.id)"
										size="small"
										:disabled="isItemRemoving(certificate.id)"
										style="display: flex; align-items: center; justify-content: center" />
								</VaPopover>
							</div>
						</div>
					</VaCardTitle>
					<!-- Contenido -->
					<VaCardContent class="certificate-content">
						<VaDivider class="content-divider" />
						<div class="certificate-info">
							<div class="info-item">
								<VaIcon name="school" class="info-icon" />
								<span class="info-label">Carrera:</span>
								<span class="info-value">{{ certificate?.data?.Carrera?.nombreCarrera + ' ' + certificate?.data?.Carrera?.clavePlan || 'No especificada' }}</span>
							</div>
							<div class="info-item">
								<VaIcon name="fingerprint" class="info-icon" />
								<span class="info-label">Número de Control:</span>
								<span class="info-value">{{ certificate?.data?.Alumno?.numeroControl || 'No especificado' }}</span>
							</div>
							<div class="info-item">
								<VaIcon name="badge" class="info-icon" />
								<span class="info-label">CURP:</span>
								<span class="info-value">{{ certificate?.data?.Alumno?.curp || 'No especificada' }}</span>
							</div>
							<div class="info-item">
								<VaIcon name="collections_bookmark" class="info-icon" />
								<span class="info-label">Tipo de Certificado:</span>
								<span class="info-value">{{ certificate?.data?.Expedicion?.tipoCertificacion || 'No especificado' }}</span>
							</div>
							<div class="info-item">
								<VaIcon name="groups" class="info-icon" />
								<span class="info-label">Responsable de Firma:</span>
								<span class="info-value">
									{{ certificate?.data?.Ipes?.Responsable?.nombre || '' }}
									{{ certificate?.data?.Ipes?.Responsable?.primerApellido || '' }}
									{{ certificate?.data?.Ipes?.Responsable?.segundoApellido || '' }}
								</span>
							</div>
							<div class="info-item">
								<VaIcon name="event" class="info-icon" />
								<span class="info-label">Fecha de Registro:</span>
								<span class="info-value">{{ certificate?.data?.Expedicion?.fecha ? formatDate(certificate.data.Expedicion.fecha) : 'No especificada' }}</span>
							</div>
						</div>
						<!-- Estado -->
						<div v-if="!certificate.success && !certificate.error && !certificate.warning" class="status-badge status-pending">
							<VaIcon name="pending" class="status-icon spin" />
							<span>Esperando validación</span>
						</div>

						<!-- Error Alert -->
						<VaAlert v-if="certificate.error" color="danger" class="status-alert">
							<template #icon>
								<VaIcon name="error" />
							</template>
							<p class="error-message-pre-wrap">{{ certificate.error.message }}</p>
						</VaAlert>

						<!-- Warning Alert -->
						<VaAlert v-if="certificate.warning" color="warning" class="status-alert">
							<template #icon>
								<VaIcon name="warning" />
							</template>
							<p class="error-message-pre-wrap">{{ certificate.warning.message }}</p>
						</VaAlert>
						<!-- ID -->
						<div class="certificate-id">
							<sub>{{ certificate.id }}</sub>
						</div>
					</VaCardContent>
				</VaCard>
			</div>

			<!-- Spacer final para mantener el scroll correcto -->
			<div class="virtualized-spacer" :style="{ height: `${(certificates.length - endIndex) * itemHeight}px` }"></div>
		</div>

		<!-- Modal -->
		<NewCertificateModal
			v-model="showNewCertificateModal"
			:institution="props.institution"
			:isTestMode="props.testMode"
			:certificate="selectedCertificate"
			:currentInstitution="props.currentInstitution"
			:isEdit="true" />
	</div>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.error-message-pre-wrap {
	white-space: pre-wrap; /* Respeta \n y hace wrap del texto largo */
	word-break: break-word; /* Rompe palabras muy largas para no desbordar */
	max-width: 100%; /* Opcional: que el contenedor limite el ancho */
}
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.virtualized-content {
	min-height: 320px;
	/* Optimizaciones de rendimiento */
	contain: layout style paint;
	will-change: transform;
}

/* Estados de eliminación optimizados */
.certificate-card.removing {
	opacity: 0.6;
	pointer-events: none;
	position: relative;
	/* Optimizaciones de rendimiento */
	contain: layout style paint;
	transform: translateZ(0); /* Forzar aceleración por hardware */
}

/* Status Badge */
.status-badge {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 2px;
	padding: 8px 16px;
	border-radius: 12px;
	font-size: 10px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.25px;
	margin-bottom: 10px;
}

.status-pending {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	color: #92400e;
	border: 1px solid #fcd34d;
}

.status-icon {
	font-size: 12px;
}

.status-alert {
	margin-bottom: 10px;
	border-radius: 8px;
}

/* Estilos específicos para alertas de advertencia */
.status-alert[color='warning'] {
	background-color: #fefce8;
	border: 1px solid #fef3c7;
	color: #1f2937;
}

.status-alert[color='warning'] :deep(.va-alert__content) {
	color: #1f2937;
}

.status-alert[color='warning'] :deep(.va-alert__icon) {
	color: #f59e0b;
}

.spin {
	animation: spin 1s linear infinite;
}

.removing-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10;
	border-radius: 12px;
}

.removing-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	color: #dc3545;
	font-weight: 600;
}

.removing-spinner {
	width: 24px;
	height: 24px;
	border: 3px solid rgba(220, 53, 69, 0.3);
	border-top: 3px solid #dc3545;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

.certificate-actions.disabled {
	opacity: 0.5;
	pointer-events: none;
}

/* Estados de carga */
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem;
	gap: 1rem;
}

.loader {
	width: 40px;
	height: 40px;
	border: 4px solid rgba(0, 123, 255, 0.3);
	border-top: 4px solid #007bff;
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

.loading-text {
	color: #6c757d;
	font-size: 1rem;
	margin: 0;
}

/* Estilos específicos para CertificatesView */
.certificate-content {
	padding: 1rem;
}

.content-divider {
	margin-bottom: 1rem;
}

.certificate-info {
	margin-bottom: 0.5rem;
}

.info-item {
	gap: 0.5rem; /* Reducido el gap */
}

.info-label {
	min-width: 60px; /* Reducido para acercar el valor */
}

.info-value {
	flex: 1;
}

.certificate-id {
	text-align: right;
	margin-top: 1rem;
}

/* Animaciones optimizadas con aceleración por hardware */
@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8) translateZ(0);
	}
	to {
		opacity: 1;
		transform: scale(1) translateZ(0);
	}
}

/* Optimizaciones adicionales para mejor rendimiento */
.virtualized-container {
	/* Mejorar el rendimiento del scroll */
	overflow-anchor: none;
	scroll-behavior: auto;
}

.virtualized-grid {
	/* Optimizaciones de renderizado */
	contain: layout style paint;
}

.certificate-card {
	/* Optimizaciones de renderizado */
	contain: layout style paint;
	transform: translateZ(0);
}
</style>
